import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { ManualTemplatesQueries, ManualTemplatesTypes } from '@frontend/api-manual-templates';
import { ScheduleQueries } from '@frontend/api-schedule';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { isEmpty } from '@frontend/validation';
import { useAlert } from '@frontend/design-system';
import { ReviewAppointmentRequestFormV2 } from '../../../../../../components/ReviewAppointmentRequest/ReviewAppointmentRequestFormV2';
import { invalidateFilterForPersonSearch } from '../../../../../../utils';
import { PersonSelector } from '../../../../components/PersonSelector/PersonSelector';
import { PatientSelectorScreenEnum } from '../../../../components/PersonSelector/types';
import { PersonSelectorFormValues } from '../../../../components/PersonSelector/types';
import { useScheduleRequestModalContext } from '../Context/ScheduleRequestModalContext';
import { useScheduleRequestModalFormContext } from '../Context/ScheduleRequestModalFormContext';
import { ScheduleRequestsTrackingIds } from '../trackingIds';
import { convertToServerDate } from '../utils';
import { SourceTenantListItem } from './types';

const { ManualTemplateTypes } = ManualTemplatesTypes;

type Props = {
  appointmentWritebackSourceTenants: SourceTenantListItem[];
  isLoadingSourceTenants: boolean;
};

export const ScheduleRequestModalFormV2 = ({ appointmentWritebackSourceTenants, isLoadingSourceTenants }: Props) => {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarRequest');
  const { selectedScheduleRequest, isPaymentsEnabled } = useScheduleRequestModalContext();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const {
    personSelectorDialogProps,
    manageScheduleRequestFormDetails,
    manageScheduleRequestModalDetails,
    selectedPerson,
    personSelectorProps,
    setTemplate,
  } = useScheduleRequestModalFormContext();

  const {
    access,
    isLoading: isLoadingModalDetails,
    isSyncAppDetailsLoading,
    providers,
    workstations,
    appointmentTypes,
    selectedAppointmentDetails,
    tags,
  } = manageScheduleRequestModalDetails;

  const { isIntegratedOffice, integrations } = DataSourcesQueries.useGetAccessibleIntegrations();

  const isRealTimeAvailabilityEnabled = isIntegratedOffice && getFeatureFlagValue('schedule-real-time-availability');

  const {
    scheduleRequestApprovalForm: { values, seedValues, getFieldProps, isComplete },
    customDateField,
    customStartTimeField,
    customEndTimeField,
    isValidEndTime,
  } = manageScheduleRequestFormDetails;

  const hasAppointmentDetails = !isEmpty(selectedAppointmentDetails);

  const { mutateAsync: addWritebackCustomer, isLoading: isLoadingAddWritebackCustomer } =
    ScheduleQueries.useAddWritebackCustomer();

  const { mutateAsync: addCustomContact, isLoading: isAddingCustomContact } = ScheduleQueries.useMutateAddCustomContact(
    selectedScheduleRequest.locationId ?? ''
  );

  const { data: scheduleRequestConfirmedTemplate } = ManualTemplatesQueries.useGetManualTemplateByType({
    templateType: ManualTemplateTypes.onlineSchedulingConfirmation,
    locationId: selectedScheduleRequest.locationId ?? '',
  });

  const paidAmount = !!selectedScheduleRequest?.bookingAmount ? selectedScheduleRequest.bookingAmount : 0;

  const availableTimesPayload = useMemo(() => {
    const selectedAppointmentType = appointmentTypes.find((type) => type.id === values.appointmentType);
    const selectedAppointmentTypeDuration = selectedAppointmentType?.durationMinutes || 0;
    const startDateDayjs =
      values.dateAndTime === 'custom'
        ? dayjs(`${customDateField.value} ${customStartTimeField.value}`)
        : dayjs(values.dateAndTime);
    const endDateDayjs =
      values.dateAndTime === 'custom'
        ? dayjs(`${customDateField.value} ${customEndTimeField.value}`)
        : startDateDayjs.add(selectedAppointmentTypeDuration, 'm');
    const startDateTime = startDateDayjs.isValid() ? startDateDayjs.toISOString() : '';
    const endDateTime = endDateDayjs.isValid() ? endDateDayjs.toISOString() : '';

    return {
      appointmentTypeId: values.appointmentType,
      locationId: selectedScheduleRequest.locationId || '',
      providerId: values.provider,
      sourceTenantId: values.source,
      startDate: startDateTime,
      endDate: endDateTime,
    };
  }, [
    appointmentTypes,
    customDateField.value,
    customEndTimeField.value,
    customStartTimeField.value,
    selectedScheduleRequest.locationId,
    values.appointmentType,
    values.dateAndTime,
    values.provider,
    values.source,
  ]);

  // Fetch availability
  const availableTimesQuery = ScheduleQueries.useListAppointmentTypeTimes(availableTimesPayload, {
    enabled: isRealTimeAvailabilityEnabled,
  });

  const { filteredProviderList, filteredWorkstationList, allValidWorkstationList } = useMemo(() => {
    const availableTimes = availableTimesQuery.data?.data ?? [];
    const filteredProviders = isIntegratedOffice ? providers?.filter((provider) => provider.sourceId) : providers;
    const allValidWorkstations = isIntegratedOffice
      ? workstations?.filter((workstation) => workstation.workstationName && workstation.sourceId)
      : workstations;

    // Extract unique operatory IDs from available times
    // TODO: use correct type when backend schema is ready
    const availableOperatoryIds = new Set(availableTimes.flatMap((timeSlot) => timeSlot.operatoryIds || []));

    // Filter workstations based on available operatory IDs
    const availableWorkstations =
      allValidWorkstations?.filter((workstation) => availableOperatoryIds.has(workstation.externalId || '')) || [];

    const filteredWorkstations = isRealTimeAvailabilityEnabled
      ? { available: availableWorkstations, all: allValidWorkstations }
      : { available: [], all: allValidWorkstations };

    return {
      filteredProviderList: filteredProviders,
      filteredWorkstationList: filteredWorkstations,
      allValidWorkstationList: allValidWorkstations,
    };
  }, [availableTimesQuery.data?.data, isIntegratedOffice, providers, workstations, isRealTimeAvailabilityEnabled]);

  // Update source form field value when there is only one source location
  useEffect(() => {
    let source = '';

    if (isIntegratedOffice && appointmentWritebackSourceTenants.length > 1) {
      source =
        appointmentWritebackSourceTenants.find(
          ({ sourceTenantId }) => selectedPerson?.SourceTenantID === sourceTenantId
        )?.sourceTenantId || '';
    } else if (isIntegratedOffice && appointmentWritebackSourceTenants.length === 1) {
      source = appointmentWritebackSourceTenants[0].sourceTenantId || '';
    } else if (!isIntegratedOffice && integrations?.length === 1) {
      source = integrations?.[0]?.SourceID;
    }

    if (source) {
      // instead of seeding the value, we are dispatching an onChange event to trigger the fieldStateReducer for workstation required state
      getFieldProps('source').onChange({ name: 'source', value: source });
    }
  }, [selectedPerson, isIntegratedOffice, appointmentWritebackSourceTenants.length, integrations?.length]);

  // Handle workstation field
  useEffect(() => {
    // Auto-select workstation if there's only one total workstation across both lists
    if (allValidWorkstationList.length === 1) {
      seedValues({
        ...values,
        workstation: allValidWorkstationList[0]?.id,
      });
    } else if (values.workstation) {
      // If the currently selected workstation is not available in the filtered list, reset it.
      // For direct availability sources, check only in the real-time available list
      const workstationList = isRealTimeAvailabilityEnabled
        ? [...filteredWorkstationList.available]
        : allValidWorkstationList;

      const isWorkstationAvailable = workstationList.some(({ id }) => id === values.workstation);

      if (!isWorkstationAvailable) {
        seedValues({
          ...values,
          workstation: '',
        });
      }
    }
  }, [allValidWorkstationList, filteredWorkstationList.available, isRealTimeAvailabilityEnabled]);

  // Seed form values using selectedAppointmentDetails
  useEffect(() => {
    if (hasAppointmentDetails) {
      seedValues({
        ...values,
        provider: selectedAppointmentDetails.providerId,
        appointmentType: selectedAppointmentDetails.appointmentId,
        workstation: selectedAppointmentDetails.workstationId,
      });
    }
  }, [
    hasAppointmentDetails,
    selectedAppointmentDetails.providerId,
    selectedAppointmentDetails.appointmentId,
    selectedAppointmentDetails.workstationId,
  ]);

  const handleAddCustomContact = async (formValues: PersonSelectorFormValues) => {
    return await addCustomContact({
      first_name: formValues?.firstName ?? '',
      last_name: formValues?.lastName ?? '',
      phone_mobile: formValues?.phoneNumber ?? '',
      birthdate: formValues?.dateOfBirth ? convertToServerDate(formValues?.dateOfBirth) : '',
      email: formValues?.email ?? '',
      gender: formValues?.gender ?? '',
    });
  };

  const handleAddWritebackContact = async (formValues: PersonSelectorFormValues) => {
    const clientLocationId =
      formValues?.clientLocationId ||
      access?.ClientLocations?.find((clientLocation) => clientLocation?.SourceID === formValues?.source)
        ?.ClientLocationID;
    return await addWritebackCustomer({
      ClientLocationID: clientLocationId ?? '',
      LocationID: selectedScheduleRequest?.locationId ?? '',
      SourceID: formValues?.source ?? '',
      Person: {
        FirstName: formValues?.firstName ?? '',
        LastName: formValues?.lastName ?? '',
        MobilePhone: formValues?.phoneNumber ?? '',
        Email: formValues?.email ?? '',
        Gender: formValues?.gender ?? '',
        Birthdate: formValues?.dateOfBirth ? convertToServerDate(formValues?.dateOfBirth) : '',
        Address1: formValues?.street || selectedScheduleRequest?.schedulee?.address?.street || '',
        Address2: selectedScheduleRequest?.schedulee?.address?.unit || '',
        City: formValues?.city || selectedScheduleRequest?.schedulee?.address?.city || '',
        State: formValues?.state || selectedScheduleRequest?.schedulee?.address?.state || '',
        PostalCode: formValues?.postalCode || selectedScheduleRequest?.schedulee?.address?.postalCode || '',
        HomePhone: '',
        MaritalStatus: '',
        MiddleName: '',
        PreferredName: '',
        ReferralSource: '',
        SSN: '',
        WorkPhone: '',
      },
    });
  };

  const handleAddCustomerContact = async (formValues: PersonSelectorFormValues) => {
    try {
      const isCustomContactSource =
        integrations?.find((integration) => integration.SourceID === formValues.source)?.SourceType === 'CustomContact';

      const response =
        isIntegratedOffice && !isCustomContactSource
          ? await handleAddWritebackContact(formValues)
          : await handleAddCustomContact(formValues);
      if (response) {
        alert.success(t('Customer successfully created.'));
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            invalidateFilterForPersonSearch(queryKey, formValues, selectedScheduleRequest.locationId),
        });
        personSelectorProps.setPersonSelectorScreen(PatientSelectorScreenEnum.PERSON_SEARCH);
      }
    } catch (e) {
      console.error(e);
      alert.error(t('We were unable to create the customer.'));
    }
  };

  // On changing source, clear the provider and workstation values if the selected value is not available in the new source
  useEffect(() => {
    if (values.source) {
      const currentProviderIndex = filteredProviderList?.findIndex(({ id }) => id === values.provider);
      const currentWorkstationIndex = [
        ...(filteredWorkstationList?.available ?? []),
        ...(filteredWorkstationList?.all ?? []),
      ].findIndex(({ id }) => id === values.workstation);

      seedValues({
        ...values,
        ...(currentProviderIndex === -1 && { provider: '' }),
        ...(currentWorkstationIndex === -1 && { workstation: '' }),
      });
    }
  }, [values.source]);

  const isLoading = isLoadingModalDetails || isSyncAppDetailsLoading || isLoadingSourceTenants;
  const showSourceLocationDropdown = isIntegratedOffice && appointmentWritebackSourceTenants.length > 1;
  const showDataSourceDropdown = !isIntegratedOffice && (integrations ?? []).length > 1;
  const showWorkstationDropdown = allValidWorkstationList.length > 1;

  const disabledForm = !selectedPerson?.PersonID;
  const patientName = selectedPerson
    ? getFullName(selectedPerson)
    : getFullName(selectedScheduleRequest.schedulee ?? {});

  return (
    <>
      <ReviewAppointmentRequestFormV2
        appointmentTypes={appointmentTypes}
        isLoading={isLoading}
        dateField={customDateField}
        startTimeField={customStartTimeField}
        endTimeField={customEndTimeField}
        isValidCustomEndTime={isValidEndTime}
        disabledForm={disabledForm}
        getFieldProps={getFieldProps}
        patientName={patientName}
        patientPhone={formatPhoneNumber(selectedPerson?.MobilePhone ?? selectedScheduleRequest?.schedulee?.phoneNumber)}
        personDialogProps={personSelectorDialogProps}
        providerList={filteredProviderList}
        selectedScheduleRequest={selectedScheduleRequest}
        showSourceLocationDropdown={showSourceLocationDropdown}
        showDataSourceDropdown={showDataSourceDropdown}
        showWorkstationDropdown={showWorkstationDropdown}
        showGroupedWorkstations={isRealTimeAvailabilityEnabled}
        isLoadingAvailableWorkstations={availableTimesQuery.isLoading}
        appointmentWritebackSourceTenants={appointmentWritebackSourceTenants}
        syncAppDataSources={integrations}
        values={values}
        workstationList={filteredWorkstationList}
        template={scheduleRequestConfirmedTemplate?.Template ?? ''}
        templateTags={tags}
        isReadOnlyTemplate={!isComplete || disabledForm}
        onTemplateChange={(template: string) => setTemplate(template)}
        isPaymentsEnabled={isPaymentsEnabled}
        paidAmount={paidAmount}
        customFieldMetaData={selectedScheduleRequest?.schedulee?.insuranceInfo}
      />
      <PersonSelector
        shouldAddAndLink={false}
        disableCustomContacts={isIntegratedOffice}
        addContactScreenTitle={t('Add Patient')}
        defaultSearchValue={selectedScheduleRequest?.schedulee?.phoneNumber ?? ''}
        locationId={selectedScheduleRequest.locationId ?? ''}
        handleAddCustomerContact={handleAddCustomerContact}
        isLoading={isLoadingAddWritebackCustomer || isAddingCustomContact}
        personSelectorDialogProps={personSelectorDialogProps}
        selectedPerson={selectedPerson}
        showDataSources={true}
        addNewButtonTrackingId={ScheduleRequestsTrackingIds.personSelectorAddNewPersonBtn}
        addToListButtonTrackingId={ScheduleRequestsTrackingIds.personSelectorAddToListBtn}
        {...personSelectorProps}
      />
    </>
  );
};
